import React, { useState, useEffect } from 'react'
import { Button, Icon, Tag } from 'antd'
import { ImagePreview } from '../../../../../../../components'
import './OutfitDetailView.scss'

/**
 * Detail view showing all outfit angles with thumbnails sidebar
 *
 * @param {Object} props
 * @param {Object} props.outfit - Selected outfit
 * @param {Object} props.activeView - Currently selected angle {label, url}
 * @param {Function} props.onViewChange - Angle selection handler
 * @param {Function} props.onApply - Apply button handler (select mode)
 * @param {Function} props.onBack - Back button handler
 * @param {Function} props.onClose - Close handler (view mode)
 * @param {String} props.mode - 'view' or 'select'
 */
const OutfitDetailView = ({
  outfit,
  activeView: externalActiveView,
  onViewChange,
  onApply,
  onBack,
  onClose,
  mode = 'select'
}) => {
  const [internalActiveView, setInternalActiveView] = useState(null)
  const [activeView, setActiveView] = useState(null)

  // Use external or internal active view
  useEffect(() => {
    setActiveView(externalActiveView || internalActiveView)
  }, [externalActiveView, internalActiveView])

  // Build all views array from outfit
  const getAllViews = () => {
    if (!outfit) return []

    const views = outfit.views ? Object.entries(outfit.views) : []
    return [
      { label: 'Primary', url: outfit.primary_image },
      ...views.map(([label, url]) => ({ label, url }))
    ]
  }

  const allViews = getAllViews()

  // Set initial active view
  useEffect(() => {
    if (outfit && !activeView) {
      if (outfit.views && Object.keys(outfit.views).length > 0) {
        const views = Object.entries(outfit.views)
        const front = views.find(([label]) =>
          label.toLowerCase().includes('front')
        )
        const initialView = front
          ? { label: front[0], url: front[1] }
          : { label: views[0][0], url: views[0][1] }

        if (onViewChange) {
          onViewChange(initialView)
        } else {
          setInternalActiveView(initialView)
        }
      } else {
        const primaryView = { label: 'Primary', url: outfit.primary_image }
        if (onViewChange) {
          onViewChange(primaryView)
        } else {
          setInternalActiveView(primaryView)
        }
      }
    }
  }, [outfit])

  const handleViewSelect = view => {
    if (onViewChange) {
      onViewChange(view)
    } else {
      setInternalActiveView(view)
    }
  }

  if (!outfit) return null

  return (
    <div className="outfit-detail-view">
      <div className="detail-header">
        {onBack && (
          <div className="back-link" onClick={onBack}>
            <Icon type="arrow-left" /> Back to library
          </div>
        )}
        <h2 className="detail-title">Viewing: {outfit.name || outfit.tag}</h2>
      </div>

      <div className="detail-body">
        <div className="thumbnail-sidebar">
          {allViews.map((view, index) => (
            <div
              key={index}
              className={`thumbnail-item ${
                activeView?.label === view.label ? 'active' : ''
              }`}
              onClick={() => handleViewSelect(view)}
            >
              <img
                key={view.url}
                src={view.url}
                alt={`${outfit.name} - ${view.label}`}
                className="thumbnail-image"
              />
              <span className="thumbnail-label">{view.label}</span>
            </div>
          ))}
        </div>

        <div className="main-content">
          <div className="image-section">
            {activeView && (
              <ImagePreview
                key={activeView.url}
                src={activeView.url}
                alt={`${outfit.name} - ${activeView.label}`}
                className="main-preview"
              />
            )}
          </div>

          <div className="info-section">
            <div className="outfit-header">
              <span className="outfit-id-tag">#{outfit.tag || outfit.id}</span>
              <h3>{outfit.name || 'Untitled Outfit'}</h3>
            </div>
            <p className="outfit-description">
              {outfit.description || 'No description available'}
            </p>
            <div className="metadata">
              <div className="metadata-item">
                <span className="label">Style</span>
                <span className="value">{outfit.style || 'Casual'}</span>
              </div>
              <div className="metadata-item">
                <span className="label">Season</span>
                <span className="value">{outfit.season || 'Year Round'}</span>
              </div>
              <div className="metadata-item">
                <span className="label">View</span>
                <span className="value">
                  <Tag color="#5057d5">{activeView?.label.toUpperCase()}</Tag>
                </span>
              </div>
            </div>

            {mode === 'select' && onApply && (
              <Button
                type="primary"
                size="large"
                onClick={() => onApply(activeView, outfit)}
                block
              >
                Assign this Outfit
              </Button>
            )}
            {mode === 'view' && onClose && (
              <Button type="default" size="large" onClick={onClose} block>
                Close Preview
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OutfitDetailView
